import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Twitter, Facebook, Send, Share2, Trophy, Star, Zap, Heart, Users, Target, Crown, Sparkles, Gift, ShoppingBag, CheckCircle, Lock } from "lucide-react";

interface GiftParticipation {
  id: string;
  request_id: string;
  has_paid: boolean;
  is_organizer: boolean;
  request: {
    status: string;
    is_gift: boolean;
    total_price: number;
  };
}

interface BadgeData {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  earned: boolean;
  progress?: number;
  maxProgress?: number;
  shareText: string;
}

interface GamificationBadgesProps {
  gifts: GiftParticipation[];
  userName?: string;
  title?: string;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function GamificationBadges({ gifts, userName, title = "Your Achievements" }: GamificationBadgesProps) {
  const [selectedBadge, setSelectedBadge] = useState<BadgeData | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Calculate stats
  const totalParticipations = gifts.length;
  const completedPayments = gifts.filter(g => g.has_paid).length;
  const completedPurchases = gifts.filter(g => g.request.status === "completed").length;
  const giftContributions = gifts.filter(g => g.request.is_gift).length;
  const groupPurchases = gifts.filter(g => !g.request.is_gift).length;
  const organizerCount = gifts.filter(g => g.is_organizer).length;
  const totalValue = gifts.reduce((sum, g) => sum + (g.has_paid ? g.request.total_price / 5 : 0), 0); // Approximate share

  // Define badges
  const badges: BadgeData[] = [
    {
      id: "first_split",
      name: "First Split! 🎉",
      description: "Joined your first group purchase",
      icon: <Sparkles className="w-5 h-5" />,
      color: "text-amber-500",
      bgColor: "bg-amber-100 dark:bg-amber-900/30",
      earned: totalParticipations >= 1,
      shareText: `I just joined my first group purchase on Shakatah! 🎉 #GroupBuying #Shakatah`
    },
    {
      id: "payment_pro",
      name: "Payment Pro 💳",
      description: "Completed 5 payments on time",
      icon: <CheckCircle className="w-5 h-5" />,
      color: "text-green-500",
      bgColor: "bg-green-100 dark:bg-green-900/30",
      earned: completedPayments >= 5,
      progress: Math.min(completedPayments, 5),
      maxProgress: 5,
      shareText: `I'm a Payment Pro on Shakatah! 💳 Completed ${completedPayments} payments on time. #ReliableFriend #Shakatah`
    },
    {
      id: "gift_giver",
      name: "Gift Giver 🎁",
      description: "Contributed to 3 group gifts",
      icon: <Gift className="w-5 h-5" />,
      color: "text-pink-500",
      bgColor: "bg-pink-100 dark:bg-pink-900/30",
      earned: giftContributions >= 3,
      progress: Math.min(giftContributions, 3),
      maxProgress: 3,
      shareText: `I'm a Gift Giver on Shakatah! 🎁 Contributed to ${giftContributions} group gifts. #GroupGifting #Shakatah`
    },
    {
      id: "squad_member",
      name: "Squad Member 👥",
      description: "Participated in 10 group purchases",
      icon: <Users className="w-5 h-5" />,
      color: "text-blue-500",
      bgColor: "bg-blue-100 dark:bg-blue-900/30",
      earned: totalParticipations >= 10,
      progress: Math.min(totalParticipations, 10),
      maxProgress: 10,
      shareText: `Squad goals achieved! 👥 I've participated in ${totalParticipations} group purchases on Shakatah. #SquadGoals #Shakatah`
    },
    {
      id: "deal_hunter",
      name: "Deal Hunter 🎯",
      description: "Completed 3 group purchases",
      icon: <Target className="w-5 h-5" />,
      color: "text-orange-500",
      bgColor: "bg-orange-100 dark:bg-orange-900/30",
      earned: completedPurchases >= 3,
      progress: Math.min(completedPurchases, 3),
      maxProgress: 3,
      shareText: `I'm a Deal Hunter! 🎯 Completed ${completedPurchases} group purchases on Shakatah. #SmartShopper #Shakatah`
    },
    {
      id: "organizer",
      name: "Ring Leader 👑",
      description: "Organized a group purchase",
      icon: <Crown className="w-5 h-5" />,
      color: "text-purple-500",
      bgColor: "bg-purple-100 dark:bg-purple-900/30",
      earned: organizerCount >= 1,
      shareText: `I'm a Ring Leader on Shakatah! 👑 Organized group purchases for my squad. #Leader #Shakatah`
    },
    {
      id: "super_saver",
      name: "Super Saver ⚡",
      description: "Saved over ₹5,000 with splits",
      icon: <Zap className="w-5 h-5" />,
      color: "text-yellow-500",
      bgColor: "bg-yellow-100 dark:bg-yellow-900/30",
      earned: totalValue >= 5000,
      progress: Math.min(Math.floor(totalValue / 1000), 5),
      maxProgress: 5,
      shareText: `Super Saver mode activated! ⚡ I've split and saved with my friends on Shakatah. #Savings #Shakatah`
    },
    {
      id: "loyal_buyer",
      name: "Loyal Buyer ⭐",
      description: "100% payment success rate (5+ purchases)",
      icon: <Star className="w-5 h-5" />,
      color: "text-indigo-500",
      bgColor: "bg-indigo-100 dark:bg-indigo-900/30",
      earned: totalParticipations >= 5 && completedPayments === totalParticipations,
      shareText: `I'm a Loyal Buyer with 100% payment success! ⭐ Always reliable on Shakatah. #Reliable #Shakatah`
    },
  ];

  const earnedBadges = badges.filter(b => b.earned);
  const lockedBadges = badges.filter(b => !b.earned);

  const handleShare = (badge: BadgeData, platform: 'twitter' | 'facebook') => {
    const url = window.location.origin + '/my-purchases';
    const text = badge.shareText;

    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank');
        break;
    }
    setShowShareDialog(false);
    setSelectedBadge(null);
  };

  const openShareDialog = (badge: BadgeData) => {
    setSelectedBadge(badge);
    setShowShareDialog(true);
  };

  if (gifts.length === 0) {
    return (
      <Card className="border-customer/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-customer" />
            <h3 className="font-semibold text-foreground">{title}</h3>
          </div>
          <p className="text-sm text-muted-foreground text-center py-4">
            Complete your first group purchase to start earning badges! 🏆
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-customer/20 overflow-hidden">
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-customer/5 to-purple-100/30 dark:from-customer/10 dark:to-purple-950/20 px-4 py-3 border-b border-customer/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-customer" />
                <h3 className="font-semibold text-foreground">{title}</h3>
              </div>
              <Badge variant="secondary" className="bg-customer/10 text-customer">
                {earnedBadges.length}/{badges.length}
              </Badge>
            </div>
          </div>

          {/* Earned Badges */}
          <div className="p-4">
            {earnedBadges.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Earned</p>
                <div className="grid grid-cols-2 gap-2">
                  {earnedBadges.map((badge) => (
                    <TooltipProvider key={badge.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => openShareDialog(badge)}
                            className={`${badge.bgColor} p-3 rounded-xl transition-all hover:scale-105 hover:shadow-md group cursor-pointer text-left`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <div className={badge.color}>{badge.icon}</div>
                              <Share2 className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                            </div>
                            <p className="text-xs font-medium text-foreground truncate">{badge.name}</p>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          <p className="font-medium">{badge.name}</p>
                          <p className="text-xs text-muted-foreground">{badge.description}</p>
                          <p className="text-xs text-customer mt-1">Click to share!</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            )}

            {/* Locked Badges */}
            {lockedBadges.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Keep Going</p>
                <div className="grid grid-cols-2 gap-2">
                  {lockedBadges.slice(0, 4).map((badge) => (
                    <TooltipProvider key={badge.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="bg-muted/50 p-3 rounded-xl opacity-60 relative">
                            <div className="flex items-center gap-2 mb-1">
                              <Lock className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <p className="text-xs font-medium text-muted-foreground truncate">{badge.name}</p>
                            {badge.progress !== undefined && badge.maxProgress && (
                              <div className="mt-1.5">
                                <div className="h-1 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-customer/50 rounded-full transition-all"
                                    style={{ width: `${(badge.progress / badge.maxProgress) * 100}%` }}
                                  />
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-0.5">{badge.progress}/{badge.maxProgress}</p>
                              </div>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          <p className="font-medium">{badge.name}</p>
                          <p className="text-xs text-muted-foreground">{badge.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-customer" />
              Share Your Achievement
            </DialogTitle>
            <DialogDescription>
              Show off your {selectedBadge?.name} badge to your friends!
            </DialogDescription>
          </DialogHeader>

          {selectedBadge && (
            <div className="space-y-4">
              {/* Badge Preview */}
              <div className={`${selectedBadge.bgColor} p-6 rounded-xl text-center`}>
                <div className={`${selectedBadge.color} inline-block mb-2`}>
                  {selectedBadge.icon}
                </div>
                <h4 className="font-bold text-lg text-foreground">{selectedBadge.name}</h4>
                <p className="text-sm text-muted-foreground mt-1">{selectedBadge.description}</p>
              </div>

              {/* Share Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-900/20"
                  onClick={() => handleShare(selectedBadge, 'twitter')}
                >
                  <Twitter className="w-5 h-5 text-blue-500" />
                  <span className="text-xs">X / Twitter</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-900/20"
                  onClick={() => handleShare(selectedBadge, 'facebook')}
                >
                  <Facebook className="w-5 h-5 text-blue-600" />
                  <span className="text-xs">Facebook</span>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
